import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/auth-context';
import userService from '../services/user-service';
import { UserPreferences } from '../models/user';
import { getFirebaseInstances } from '../firebase/firebase';
import { log } from '../services/production-logger';


// Default preferences
const defaultPreferences: UserPreferences = {
  theme: 'system',
  language: 'en',
  defaultCurrency: 'USD',
  notifications: {
    email: true,
    push: true,
    inApp: true,
    invoiceCreated: true,
    paymentReceived: true,
    paymentOverdue: true,
    subscriptionRenewal: true,
    memberActivity: true,
  },
  privacy: {
    allowMemberDataSharing: true,
    allowAnonymousStatistics: true,
    profileVisibility: 'organization',
  },
  account: {
    twoFactorEnabled: false,
    emailVerificationRequired: true,
  },
};

// Storage key for local cache
const PREFERENCES_STORAGE_KEY = 'user_preferences_cache';

/**
 * Custom hook for managing user preferences
 * Syncs preferences with Firestore and maintains local cache
 */
export const useUserPreferences = () => {
  const { currentUser } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load cached preferences
  const loadCachedPreferences = useCallback(async () => {
    try {
      const cached = await AsyncStorage.getItem(PREFERENCES_STORAGE_KEY);
      if (cached) {
        setPreferences(JSON.parse(cached));
      }
    } catch (error) {
      log.error('Error loading cached preferences:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load preferences from Firestore and set up real-time listener
  useEffect(() => {
    if (!currentUser?.uid) {
      setPreferences(defaultPreferences);
      setIsLoading(false);
      return;
    }

    const { firestore } = getFirebaseInstances();

    // Set up real-time listener for user preferences
    const unsubscribe = onSnapshot(
      doc(firestore, 'users', currentUser.uid),
      snapshot => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          const userPrefs = userData.preferences || {};

          // Merge with defaults to ensure all fields exist
          const mergedPrefs: UserPreferences = {
            ...defaultPreferences,
            ...userPrefs,
            notifications: {
              ...defaultPreferences.notifications,
              ...(userPrefs.notifications || {}),
            },
            privacy: {
              ...defaultPreferences.privacy,
              ...(userPrefs.privacy || {}),
            },
            account: {
              ...defaultPreferences.account,
              ...(userPrefs.account || {}),
            },
          };

          setPreferences(mergedPrefs);

          // Cache preferences locally for offline access
          AsyncStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(mergedPrefs)).catch(error =>
            log.warn('Failed to cache preferences:', error),
          );
        }
        setIsLoading(false);
      },
      error => {
        log.error('Error listening to preferences:', error);
        // Fall back to cached preferences
        loadCachedPreferences();
      },
    );

    return () => unsubscribe();
  }, [currentUser?.uid, loadCachedPreferences]);

  // Update preferences in Firestore
  const updatePreferences = useCallback(
    async (newPrefs: Partial<UserPreferences>) => {
      if (!currentUser?.uid) {
        log.warn('Cannot update preferences: No user logged in');
        return false;
      }

      setIsSyncing(true);
      try {
        // Optimistically update local state
        const updatedPrefs = { ...preferences, ...newPrefs };
        setPreferences(updatedPrefs);

        // Update in Firestore
        await userService.updateUserProfileFields(currentUser.uid, {
          preferences: updatedPrefs,
        });

        return true;
      } catch (error) {
        log.error('Error updating preferences:', error);
        // Revert on error
        setPreferences(preferences);
        return false;
      } finally {
        setIsSyncing(false);
      }
    },
    [currentUser?.uid, preferences],
  );

  // Update specific preference sections
  const updateTheme = useCallback(
    (theme: 'light' | 'dark' | 'system') => updatePreferences({ theme }),
    [updatePreferences],
  );

  const updateLanguage = useCallback(
    (language: string) => updatePreferences({ language }),
    [updatePreferences],
  );

  const updateNotifications = useCallback(
    (notifications: Partial<UserPreferences['notifications']>) =>
      updatePreferences({
        notifications: {
          ...preferences.notifications,
          ...notifications,
        },
      }),
    [updatePreferences, preferences.notifications],
  );

  const updatePrivacy = useCallback(
    (privacy: Partial<UserPreferences['privacy']>) =>
      updatePreferences({
        privacy: {
          ...preferences.privacy,
          ...privacy,
        },
      }),
    [updatePreferences, preferences.privacy],
  );

  const updateAccount = useCallback(
    (account: Partial<UserPreferences['account']>) =>
      updatePreferences({
        account: {
          ...preferences.account,
          ...account,
        },
      }),
    [updatePreferences, preferences.account],
  );

  // Reset preferences to defaults
  const resetPreferences = useCallback(async () => {
    if (!currentUser?.uid) {
      return false;
    }

    setIsSyncing(true);
    try {
      await userService.updateUserProfileFields(currentUser.uid, {
        preferences: defaultPreferences,
      });
      return true;
    } catch (error) {
      log.error('Error resetting preferences:', error);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [currentUser?.uid]);

  return {
    preferences,
    updatePreferences,
    updateTheme,
    updateLanguage,
    updateNotifications,
    updatePrivacy,
    updateAccount,
    resetPreferences,
    isLoading,
    isSyncing,
  };
};
